import { useStateTransition } from "@codesandbox/states";
import { useEnvironmentInterface } from "environment-interface";
import { useEditorFeature } from "features/editor/Feature";
import type { VisibilityNumber } from "features/editor/sandboxUtils";
import { useReducerDevtools } from "features/utils/useReducerDevtools";
import { useSession } from "hooks/api/useSession";
import { useEffect, useReducer } from "react";
import type { Dispatch } from "react";

import { useEditorRouter } from "hooks/useEditorRouter";
import { createHookContext } from "utils/createHookContext";
import { csbApi } from "utils/csbApi";

import type { PublicAction, State } from "./reducer";
import { reducer } from "./reducer";

export type { PublicAction, State } from "./reducer";

export type Props = {
  initialState?: State;
  id: string;
  isCloud: boolean;
  onFork: (params: { alias: string; workspaceId: string | null }) => void;
};

function convertPrivacyToNumber(
  privacy: "public" | "unlisted" | "private" | undefined,
): 0 | 1 | 2 | undefined {
  if (privacy === "public") {
    return 0;
  } else if (privacy === "unlisted") {
    return 1;
  } else if (privacy === "private") {
    return 2;
  }
}

export const useForkSandbox = createHookContext(
  ({ initialState, onFork, id, isCloud }: Props) => {
    const { api, storage } = useEnvironmentInterface();
    const [, dispatchEditor] = useEditorFeature();
    const router = useEditorRouter();
    const forkFeature = useReducer(
      reducer,
      initialState || {
        state: "READY",
      },
    );

    const [session] = useSession();

    useReducerDevtools(`fork`, forkFeature);

    const [state, dispatch] = forkFeature;

    useEffect(() => api.events.subscribe(dispatch), []);

    useStateTransition(
      state,
      "FORKING",
      ({ workspaceId, collectionId, privacy: privacyFromFork }) => {
        const privacyFromParams = convertPrivacyToNumber(
          router.queryParams.privacy.get(),
        );

        let privacy: VisibilityNumber = 2;

        if (privacyFromParams && !Number.isNaN(privacyFromParams)) {
          privacy = privacyFromParams;
        } else if (!Number.isNaN(privacyFromFork)) {
          privacy = privacyFromFork;
        }

        if (session.state !== "AUTHENTICATED") {
          dispatchEditor({
            type: "SET_PREVENT_AUTHENTICATION_REFETCH",
            prevent: true,
          });
          csbApi.session.signIn().then(() => {
            dispatchEditor({
              type: "SET_PREVENT_AUTHENTICATION_REFETCH",
              prevent: false,
            });
            api.forkSandbox(id, isCloud, workspaceId, collectionId, privacy);
          });
        } else {
          api.forkSandbox(id, isCloud, workspaceId, collectionId, privacy);
        }

        // !important: Update the selected workspace
        storage.set("codesandbox-selected-team-id", workspaceId);
      },
    );

    useStateTransition(
      state,
      {
        FORKING: {
          "API:FORK_SANDBOX_SUCCESS": "READY",
        },
      },
      (_, { fork }) => {
        onFork({
          alias: fork.alias,
          workspaceId: fork.workspaceId,
        });
      },
    );

    return [state, dispatch] as [State, Dispatch<PublicAction>];
  },
);
